import React, { useState, useEffect } from "react";

interface TabItem {
    id: string;
    label: string;
    content?: React.ReactNode;
    disabled?: boolean;
}

interface TabProps {
    tabs: TabItem[];
    defaultActiveTab?: string;
    onTabChange?: (tabId: string) => void;
    showContent?: boolean;
    className?: string;
}

const Tab = ({ tabs, defaultActiveTab, onTabChange, showContent = false, className = "" }: TabProps) => {
    const [activeTab, setActiveTab] = useState(defaultActiveTab || tabs[0]?.id || "");

    // Only update activeTab when defaultActiveTab prop changes (not when internal activeTab changes)
    useEffect(() => {
        if (defaultActiveTab) {
            setActiveTab(defaultActiveTab);
        }
    }, [defaultActiveTab]);

    const handleTabClick = (tabId: string, disabled?: boolean) => {
        if (disabled) return;
        setActiveTab(tabId);
        onTabChange?.(tabId);
    };

    const activeTabContent = tabs.find((tab) => tab.id === activeTab)?.content;

    return (
        <div className={`inline-flex flex-col ${className}`}>
            <div
                role="tablist"
                className="inline-flex bg-gray-100 border border-gray-300 rounded-3xl p-1 h-[40px] w-fit mx-auto"
            >
                {tabs.map((tab, index) => (
                    <button
                        key={tab.id}
                        role="tab"
                        className={`
              px-4 text-sm font-semibold transition-all duration-200 text-base-content
              ${activeTab === tab.id ? "bg-white border border-gray-200 shadow-sm rounded-3xl" : "hover:text-gray-800"}
              ${index === 0 ? "mr-1" : ""}
              ${tab.disabled ? "opacity-50 cursor-not-allowed" : ""}
            `}
                        onClick={() => handleTabClick(tab.id, tab.disabled)}
                        aria-selected={activeTab === tab.id}
                        disabled={tab.disabled}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {showContent && activeTabContent && <div className="mt-4">{activeTabContent}</div>}
        </div>
    );
};

export default Tab;
